import { supabase } from '@/integrations/supabase/client';
import type {
  SavedReport,
  CreateSavedReportRequest,
  UpdateSavedReportRequest,
  SavedReportWithMeta,
} from '@/types/savedReports';

/**
 * Fetch all accessible saved reports (personal + team)
 * Sorted by updated_at descending
 */
export async function fetchSavedReports(): Promise<SavedReportWithMeta[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Only select metadata fields (not elements/set_definition) for list performance
  const { data, error } = await supabase
    .from('app_saved_reports')
    .select('id, name, description, visibility, owner, created_at, updated_at')
    .order('updated_at', { ascending: false });

  if (error) throw error;

  return (data || []).map(report => ({
    ...report,
    isOwner: report.owner === user.id,
  }));
}

/**
 * Fetch a single saved report by ID
 */
export async function fetchSavedReport(id: string): Promise<SavedReportWithMeta | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('app_saved_reports')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return {
    ...data,
    isOwner: data.owner === user.id,
  };
}

/**
 * Create a new saved report
 */
export async function createSavedReport(
  request: CreateSavedReportRequest
): Promise<SavedReport> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('app_saved_reports')
    .insert({
      owner: user.id,
      name: request.name,
      description: request.description || null,
      visibility: request.visibility,
      set_definition: request.set_definition,
      elements: request.elements,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update an existing saved report (owner only - enforced by RLS)
 */
export async function updateSavedReport(
  id: string,
  request: UpdateSavedReportRequest
): Promise<SavedReport> {
  const updatePayload = {
    ...(request.name !== undefined && { name: request.name }),
    ...(request.description !== undefined && { description: request.description }),
    ...(request.visibility !== undefined && { visibility: request.visibility }),
    ...(request.set_definition !== undefined && { set_definition: request.set_definition }),
    ...(request.elements !== undefined && { elements: request.elements }),
    updated_at: new Date().toISOString(), // Explicitly set updated_at since it's not auto-updated
  };

  const { data, error } = await supabase
    .from('app_saved_reports')
    .update(updatePayload)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error(`❌ [updateSavedReport] Error:`, error);
    throw error;
  }

  return data;
}

/**
 * Delete a saved report (owner only - enforced by RLS)
 */
export async function deleteSavedReport(id: string): Promise<void> {
  const { error } = await supabase
    .from('app_saved_reports')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Duplicate a report as a personal copy
 * Can duplicate both personal and team reports
 */
export async function duplicateSavedReport(
  id: string,
  newName: string
): Promise<SavedReport> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Fetch the original report
  const original = await fetchSavedReport(id);
  if (!original) throw new Error('Report not found');

  // Create a copy with the new name, always personal
  return createSavedReport({
    name: newName,
    description: original.description || undefined,
    visibility: 'personal',
    set_definition: original.set_definition,
    elements: original.elements,
  });
}
